/**
 * CeremonyPieces — the small shared parts of both ceremonies.
 *
 * They live together because they are the same three shapes on both the driver route and
 * the witness route, and because a confirm-this-statement control that looks different on
 * the two pages is a control two people would describe differently on a phone call.
 */

import type { ReactNode } from 'react';
import { Check, Lock } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/** Small caps label above a step's heading. */
export function Eyebrow({ children }: { children: ReactNode }) {
    return (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {children}
        </p>
    );
}

export function StepHeading({ children }: { children: ReactNode }) {
    return (
        <h2 className="mt-1.5 text-xl font-bold tracking-tight text-foreground">{children}</h2>
    );
}

/** The frozen / reference strip. */
export function FrozenNote({ children }: { children: ReactNode }) {
    return (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-[11.5px] text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            <span>{children}</span>
        </div>
    );
}

/** A numbered plain-English key term on the intro step. */
export function KeyTermRow({
    index,
    summary,
    detail,
}: {
    index: number;
    summary: string;
    detail?: string;
}) {
    return (
        <div className="flex gap-3 border-b border-border py-3.5 last:border-b-0">
            <span className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-foreground text-[10.5px] font-bold text-background">
                {index}
            </span>
            <div className="min-w-0">
                <p className="text-[13.5px] leading-snug text-foreground">{summary}</p>
                {/* A term whose supporting fact the payload does not carry states the term and
                    stops there, rather than filling the line with an approximation. */}
                {detail && (
                    <span className="mt-0.5 block text-[11.5px] text-muted-foreground">{detail}</span>
                )}
            </div>
        </div>
    );
}

/** Tick-to-confirm. The whole row is the control; the label is not decoration. */
export function ConfirmRow({
    checked,
    onCheckedChange,
    title,
    detail,
    disabled,
}: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    title: string;
    detail?: string;
    disabled?: boolean;
}) {
    return (
        <label
            className={cn(
                'mb-2 flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors',
                checked ? 'border-foreground/25 bg-muted' : 'border-border hover:border-foreground/20',
                disabled && 'cursor-not-allowed opacity-60',
            )}
        >
            <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={(value) => onCheckedChange(value === true)}
                className="mt-0.5 h-[18px] w-[18px] rounded-[6px]"
            />
            <span className="min-w-0 text-[13px] leading-snug">
                <b className="font-semibold text-foreground">{title}</b>
                {detail && (
                    <em className="mt-0.5 block not-italic text-[11.5px] text-muted-foreground">
                        {detail}
                    </em>
                )}
            </span>
        </label>
    );
}

/** Read-only "signing as" panel above the pad. */
export function SigningAs({ name, sub }: { name: string; sub?: string | null }) {
    return (
        <div className="rounded-xl border border-border bg-muted px-3.5 py-3">
            <Eyebrow>Signing as</Eyebrow>
            <p className="mt-0.5 text-sm font-semibold text-foreground">{name}</p>
            {sub && <p className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</p>}
        </div>
    );
}

/** The big tick on a done step. */
export function DoneMark() {
    return (
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-foreground text-background">
            <Check className="h-8 w-8" strokeWidth={2.5} />
        </div>
    );
}

/** One row of the "what happens next" trail. */
export function TrailRow({
    state,
    title,
    detail,
}: {
    state: 'done' | 'now' | 'later';
    title: string;
    detail: string;
}) {
    return (
        <div className="flex gap-3 py-2">
            <span
                className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    state === 'done' && 'bg-foreground',
                    state === 'now' && 'bg-foreground/40 ring-4 ring-foreground/10',
                    state === 'later' && 'bg-border',
                )}
                aria-hidden="true"
            />
            <span className="min-w-0 text-[12.5px] leading-snug">
                <b className="font-semibold text-foreground">{title}</b>
                <br />
                <span className="text-muted-foreground">{detail}</span>
            </span>
        </div>
    );
}
